import { ReactNode, useState, useEffect, useRef } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { Box, Button, Container, Flex, HStack, Link, Text, useColorMode } from '@chakra-ui/react'
import { Edit3 } from 'lucide-react'
import { UserMenu, ThemeToggle } from './headerComponents'
import { getStoredApiKey } from '@/lib/storage/local-storage'

interface HeaderProps {
  hideUserMenu?: boolean
  centerContent?: ReactNode
}

export function Header({ hideUserMenu = false, centerContent }: HeaderProps) {
  const { colorMode } = useColorMode()
  const location = useLocation()
  const isLandingPage = location.pathname === '/'
  const isEditorPage = location.pathname === '/editor'
  const isDocsPage = location.pathname.startsWith('/docs/')
  const isSettingsPage = location.pathname === '/settings'
  const isLoggedIn = !!getStoredApiKey()
  const logoDestination = isLoggedIn ? '/editor' : '/'

  // Show/hide header on scroll direction
  const [isVisible, setIsVisible] = useState(true)
  const [isScrolled, setIsScrolled] = useState(false)
  const lastScrollY = useRef(0)

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const isScrollingUp = currentScrollY < lastScrollY.current
      const isAtTop = currentScrollY < 100

      // Show header when scrolling up or at top
      setIsVisible(isScrollingUp || isAtTop)
      setIsScrolled(currentScrollY > 100)
      lastScrollY.current = currentScrollY
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // On landing page, make header transparent at top, solid when scrolled
  const headerBg = isLandingPage
    ? (isScrolled
      ? (colorMode === 'dark' ? 'rgba(12, 10, 9, 0.95)' : 'rgba(250, 250, 249, 0.95)')
      : 'transparent')
    : colorMode === 'dark'
      ? 'rgba(12, 10, 9, 0.8)'  // stone.950/80%
      : 'rgba(250, 250, 249, 0.8)'  // stone.50/80%

  return (
    <Box
      as="header"
      borderBottomWidth={isLandingPage && !isScrolled ? '0' : '1px'}
      borderColor={colorMode === 'dark' ? 'stone.700' : 'stone.200'}
      bg={headerBg}
      backdropFilter={isLandingPage && !isScrolled ? 'none' : 'blur(12px)'}
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex={50}
      h="56px"
      transform={isVisible ? 'translateY(0)' : 'translateY(-100%)'}
      transition="transform 0.3s ease, background 0.3s ease"
    >
      <Container maxW="1000px" py={2} px={{ base: 4, md: 6 }} h="full">
        <Flex align="center" justify="space-between" h="full">
          <Link as={RouterLink} to={logoDestination} _hover={{ textDecoration: 'none' }}>
            <Text
              fontFamily="heading"
              fontWeight="bold"
              fontSize="xl"
              letterSpacing="-0.025em"
              color={colorMode === 'dark' ? 'stone.50' : 'stone.900'}
            >
              myrkup
            </Text>
          </Link>
          {centerContent && (
            <Flex position="absolute" left="50%" transform="translateX(-50%)">
              {centerContent}
            </Flex>
          )}
          <HStack spacing={3}>
            {/* Show Editor button on non-editor, non-landing, non-docs pages */}
            {!isLandingPage && !isEditorPage && !isDocsPage && !isSettingsPage && (
              <Button
                as={RouterLink}
                to="/editor"
                size="sm"
                variant="ghost"
                leftIcon={<Edit3 size={14} />}
              >
                Editor
              </Button>
            )}
            {!isEditorPage && <ThemeToggle />}
            {!hideUserMenu && <UserMenu />}
          </HStack>
        </Flex>
      </Container>
    </Box>
  )
}
